<template>
  <div v-if="isVisible" class="premium-popup-overlay" tabindex="0" @click="closePopup" @keyup.esc="closePopup">
    <div class="premium-popup-container" @click.stop>
      <button type="button" class="close-button" @click="closePopup">
        <v-sprite color="" icon="close-violet" />
      </button>

      <div class="premium-popup-content">
        <div class="premium-header">
          <p class="premium-subtitle">100 € HT / mois facturé annuellement</p>
          <h1 class="premium-title">Le chasseur de tête numérique premium</h1>
        </div>

        <div class="premium-body">
          <div class="premium-image">
            <div class="image-placeholder">
              <ion-icon name="phone-portrait-outline" size="large" />
              <p>Aperçu de l'interface Premium</p>
            </div>
          </div>

          <div class="premium-features">
            <div class="feature-section">
              <div class="feature-item">
                <ion-icon name="checkmark-circle" class="feature-icon" />
                <div class="feature-content">
                  <h3>✅ Recrutement gratuit en illimité</h3>
                  <p>Accédez aux coordonnées candidats en illimité</p>
                </div>
              </div>

              <div class="feature-item">
                <ion-icon name="checkmark-circle" class="feature-icon" />
                <div class="feature-content">
                  <h3>✅ Votre marque employeur</h3>
                  <p>Créez la marque employeur qui vous ressemble et attirez encore plus de candidats :</p>
                  <ul>
                    <li>Description de votre entreprise et de vos valeurs</li>
                    <li>Photos et vidéos</li>
                    <li>Avantages salariés offerts par votre entreprise</li>
                    <li>Processus de recrutement</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="premium-footer">
          <button
            type="button"
            class="premium-cta-button"
            :disabled="isLoading"
            @click="subscribeToPremium"
          >
            <span v-if="!isLoading">Je m'abonne au compte premium</span>
            <span v-else>Redirection en cours...</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useToast } from 'vue-toast-notification';

export default defineComponent({
  name: 'PremiumPopup',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['close'],
  setup() {
    const toast = useToast();
    const isLoading = ref(false);

    return {
      isLoading,
      toast,
    };
  },

  methods: {
    closePopup(): void {
      this.$emit('close');
    },

    async subscribeToPremium(): Promise<void> {
      this.isLoading = true;

      try {
        const response = await window.ky.post('/ecommerce/premium-headhunter-checkout/').json();
        window.location.href = response.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        this.toast.error('Erreur lors de la création de la session de paiement');
        this.isLoading = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
.premium-popup-overlay {
  align-items: center;
  background: rgba(0, 0, 0, .8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 9999;
}

.premium-popup-container {
  background: #f5f4fb;
  border-radius: 12px;
  height: 100vh;
  overflow: hidden;
  position: relative;
  width: 100vw;
}

.close-button {
  background: none;
  border: 0;
  color: #666;
  cursor: pointer;
  font-size: 32px;
  position: absolute;
  right: 20px;
  top: 20px;
  z-index: 10;

  &:hover {
    color: #333;
  }
}

.premium-popup-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: 60px 40px 40px;
}

.premium-header {
  margin-bottom: 40px;
  text-align: center;
}

.premium-subtitle {
  color: #717296;
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 16px;
}

.premium-title {
  color: #000;
  font-size: 36px;
  font-weight: bold;
  line-height: 1.2;
  margin: 0;
}

.premium-body {
  align-items: start;
  display: grid;
  flex: 1;
  gap: 40px;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    gap: 60px;
    grid-template-columns: 1fr 1fr;
  }
}

.premium-image {
  align-items: center;
  display: flex;
  justify-content: center;
}

.image-placeholder {
  border: 2px dashed #ddd;
  border-radius: 12px;
  color: #666;
  padding: 40px;
  text-align: center;

  ion-icon {
    color: #7556ed;
    display: block;
    margin: 0 auto 16px;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.premium-features {
  display: flex;
  flex-direction: column;
}

.feature-section {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.feature-item {
  align-items: flex-start;
  display: flex;
  gap: 16px;

  .feature-icon {
    color: #22c55e;
    flex-shrink: 0;
    font-size: 28px;
    margin-top: 4px;
  }
}

.feature-content {
  flex: 1;

  h3 {
    color: #1f2937;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px;
  }

  p {
    color: #6b7280;
    font-size: 16px;
    line-height: 1.6;
    margin: 0 0 12px;
  }

  ul {
    color: #6b7280;
    margin: 0;
    padding-left: 20px;

    li {
      font-size: 15px;
      line-height: 1.5;
      margin-bottom: 8px;
    }
  }
}

.premium-footer {
  border-top: 1px solid #e5e7eb;
  margin-top: 20px;
  padding-top: 40px;
  text-align: center;
}

.premium-cta-button {
  background: #7556ed;
  border: 0;
  border-radius: 40px;
  color: #fff;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  min-width: 320px;
  padding: 18px 40px;
  transition: background-color .2s;

  &:disabled {
    cursor: not-allowed;
    opacity: .7;
  }

  &:hover:not(:disabled) {
    background: #6344d1;
  }
}

@media (max-width: 768px) {
  .premium-popup-container {
    border-radius: 0;
    height: 100vh;
    width: 100vw;
  }

  .premium-popup-content {
    padding: 60px 20px 40px;
  }

  .premium-title {
    font-size: 28px;
  }

  .premium-body {
    gap: 30px;
  }

  .premium-cta-button {
    min-width: 280px;
    padding: 16px 32px;
  }
}
</style>
